import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CallSplit as BranchIcon,
} from '@mui/icons-material';
import { generateId } from '../../utils/idGeneration';
import ConditionBuilder from './ConditionBuilder';

/**
 * Shown in PropertiesPanel when no component is selected.
 * Provides screen title editing and conditional routing rules.
 *
 * Props:
 *   currentScreen   – the active screen object
 *   screens         – all screens in the flow
 *   onRenameScreen  – (screenId, newTitle) => void
 *   onUpdateRouting – (screenId, routing) => void
 */
const ScreenPanel = ({ currentScreen, screens, onRenameScreen, onUpdateRouting }) => {
  if (!currentScreen) return null;

  const routing = currentScreen.routing || { mode: 'simple', rules: [], defaultNextScreenId: null };
  const otherScreens = screens.filter(s => s.id !== currentScreen.id);
  const currentIndex = screens.findIndex(s => s.id === currentScreen.id);
  const autoNextScreen = currentIndex < screens.length - 1 ? screens[currentIndex + 1] : null;
  const hasRules = routing.rules.length > 0;

  const setRouting = (patch) => onUpdateRouting(currentScreen.id, { ...routing, ...patch });

  const handleAddRule = () => {
    setRouting({
      rules: [
        ...routing.rules,
        {
          id: generateId(),
          mode: routing.mode,
          fieldName: '',
          operator: 'eq',
          value: '',
          expression: '',
          nextScreenId: '',
        },
      ],
    });
  };

  const handleUpdateRule = (ruleId, updates) => {
    setRouting({
      rules: routing.rules.map(r => r.id === ruleId ? { ...r, ...updates } : r),
    });
  };

  const handleDeleteRule = (ruleId) => {
    setRouting({ rules: routing.rules.filter(r => r.id !== ruleId) });
  };

  // Bridge between ConditionBuilder's { mode, field, operator, value, expression }
  // and the routing rule shape.
  const ruleToCondition = (rule) => ({
    mode: rule.mode || routing.mode,
    field: rule.fieldName,
    operator: rule.operator || 'eq',
    value: rule.value,
    expression: rule.expression,
  });

  const conditionUpdateToRule = (ruleId, cond) => {
    handleUpdateRule(ruleId, {
      mode: cond.mode,
      fieldName: cond.field,
      operator: cond.operator,
      value: cond.value,
      expression: cond.expression,
    });
  };

  return (
    <Box className="properties-panel" data-testid="properties-panel">
      <Typography variant="h6" gutterBottom>
        Screen
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Select a component to edit its properties
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Screen Title"
          value={currentScreen.title || ''}
          onChange={e => onRenameScreen(currentScreen.id, e.target.value)}
          helperText="Displayed at the top of the WhatsApp screen"
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Routing Rules */}
      <Accordion defaultExpanded={hasRules} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BranchIcon
              fontSize="small"
              sx={{ color: hasRules ? 'primary.main' : 'text.disabled' }}
            />
            <Typography variant="body2">
              Routing Rules
            </Typography>
            {hasRules && (
              <Chip
                label={routing.rules.length}
                size="small"
                color="primary"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0 }}>
          {currentScreen.terminal && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This is a terminal screen. Routing rules are not needed.
            </Alert>
          )}

          {!hasRules && !currentScreen.terminal && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Add a rule to navigate to a different screen based on what the user answered.
            </Typography>
          )}

          {routing.rules.map((rule, idx) => (
            <Box
              key={rule.id}
              sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Rule {idx + 1}
                </Typography>
                <IconButton size="small" onClick={() => handleDeleteRule(rule.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <ConditionBuilder
                condition={ruleToCondition(rule)}
                onChange={cond => conditionUpdateToRule(rule.id, cond)}
                screenComponents={currentScreen.components}
                label="If"
                helperText="When this rule is true, the flow goes to the screen selected below."
                screens={screens}
                currentScreenId={currentScreen.id}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>
                  →
                </Typography>
                <FormControl size="small" fullWidth>
                  <InputLabel>Go to screen</InputLabel>
                  <Select
                    value={rule.nextScreenId || ''}
                    onChange={e => handleUpdateRule(rule.id, { nextScreenId: e.target.value })}
                    label="Go to screen"
                  >
                    {otherScreens.map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          ))}

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddRule}
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
          >
            Add Routing Rule
          </Button>

          {/* Default (Otherwise) destination */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Otherwise (default)
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>Go to screen</InputLabel>
              <Select
                value={routing.defaultNextScreenId || ''}
                onChange={e => setRouting({ defaultNextScreenId: e.target.value || null })}
                label="Go to screen"
              >
                <MenuItem value="">
                  <em>
                    {autoNextScreen
                      ? `Next screen: ${autoNextScreen.title}`
                      : 'End of flow (complete)'}
                  </em>
                </MenuItem>
                {otherScreens.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ScreenPanel;
